import { z } from './utils/zod.js';
import { errorResponse, successResponse, dataResponse } from './utils/response-templates.js';
const passwordSchema = z
    .string()
    .min(8, 'Password must be at least 8 characters long')
    .max(100, 'Password must be no longer than 100 characters')
    .regex(/^.*[0-9].*$/, 'Password must include at least one number')
    .regex(/^.*[a-z].*$/, 'Password must include at least one lowercase letter')
    .regex(/^.*[A-Z].*$/, 'Password must include at least one uppercase letter');
export const createUserRequestSchema = z.object({
    email: z.string().commonEmail(),
    name: z.string().trim().min(1),
    language: z.string().trim(),
    password: passwordSchema,
});
export const userCreateResponseSchema = successResponse().extend({
    token: z.string(),
});
export const newEmailVerificationGeneratedSchema = successResponse();
export const newPhoneVerificationGeneratedSchema = successResponse();
export const userEmailExistsResponseSchema = errorResponse('User already exists').extend({
    code: z.string(),
});
export const unsupportedUserLanguageResponseSchema = errorResponse('Unsupported language');
export const confirmEmailRequestSchema = z.object({
    code: z.string().length(6),
});
export const confirmEmailResponseSchema = successResponse();
export const confirmEmailFailedResponseSchema = z.object({
    success: z.boolean().openapi({ example: false }),
    message: z.string().openapi({
        example: 'Verification failed',
    }),
    code: z.enum(['EXPIRED', 'TOO_MANY_ATTEMPTS', 'INVALID_CODE', 'NOT_FOUND']),
});
export const generatePhoneVerificationRequestSchema = z.object({
    phone: z.string().phoneNumber('Invalid phone number'),
});
export const generatePhoneVerificationResponse = successResponse();
export const confirmPhoneRequestSchema = confirmEmailRequestSchema;
export const confirmPhoneResponseSchema = successResponse();
export const confirmPhoneFailedResponseSchema = confirmEmailFailedResponseSchema;
// forgot password
export const forgotPasswordRequestSchema = z.object({
    email: z.string().commonEmail().optional(),
});
export const forgotPasswordResponseSchema = successResponse();
// password reset
export const passwordResetRequestSchema = z.object({
    password: passwordSchema,
});
export const passwordResetResponseSchema = successResponse();
// update-user-password
export const updatePasswordRequestSchema = z.object({
    oldPassword: z.string(),
    newPassword: passwordSchema,
});
export const updatePasswordForbidResponseSchema = errorResponse('Invalid passord information');
export const updatePasswordResponseSchema = successResponse();
export const loginRequestSchema = z.object({
    email: z.string().commonEmail(),
    password: z.string(),
});
export const loginSuccessResponseSchema = successResponse().extend({
    token: z.string(),
    emailVerified: z.boolean(),
    accountTypeSelected: z.boolean(),
});
export const idAustriaLoginResponse = successResponse().extend({
    token: z.string(),
});
export const loginErrorResponseSchema = errorResponse('Invalid email or password');
const userData = z.object({
    id: z.string(),
    role: z.string(),
    email: z.string(),
    name: z.string(),
    phone: z
        .object({
        number: z.string(),
        countryCode: z.string().optional(),
        formattedNumber: z.string(),
    })
        .nullable(),
    preferences: z.any(),
    kyc: z.boolean(),
    hasExternalLogin: z.boolean(),
    emailVerifiedAt: z.string().nullable(),
});
export const userSuccessResponseSchema = dataResponse(userData);
export const updateStatusEnum = z.enum(['UPDATED', 'PHONE_VERIFICATION', 'EXPIRED']);
export const updateUserDetailsRequestSchema = z.object({
    name: z.string().trim().min(1).optional(),
    phone: z.string().phoneNumber('Invalid phone number', { emptyStringAllowed: true }).optional(),
});
export const updateUserDetailsResponseSchema = successResponse().extend({
    status: updateStatusEnum,
    user: userData,
    token: z.string().optional(),
});
export const userDataUpdateVerificationRequestSchema = z.object({
    code: z.string().length(6),
});
export const idAustriaLoginRequest = z.object({
    code: z.string(),
    // Optional: the exact redirect_uri the frontend used at /authorize. The
    // backend must echo this verbatim to /token, otherwise the IdP rejects the
    // code exchange with `invalid_grant`. When omitted, the backend falls back
    // to its env config (works for production where origin is fixed).
    redirectUri: z.string().optional(),
});
export const idAustriaLoginSuccess = successResponse().extend({
    token: z.string(),
});
export const idAustriaFinishLoginRequest = z.object({
    id: z.string(),
    email: z.string(),
    language: z.string(),
});
export const idAustriaLoginToRegisterResponse = errorResponse('User needs registration').extend({
    code: z.string(),
    signupId: z.string(),
});
export const tokenInvalidResponseSchema = errorResponse('Invalid or expired token');
export const noPendingUpdateResponseSchema = errorResponse('No pending update found');
// PIN creation
export const createPinRequestSchema = z.object({
    pin: z.string().regex(/^\d+$/, 'PIN must contain only digits'),
});
export const pinCreatedResponseSchema = successResponse();
export const pinIdentityNotFoundResponseSchema = errorResponse('Identity record not found or PIN already set');
export const pinUserNotExternalResponseSchema = errorResponse('PIN can only be set for users with external login');
// PIN update
export const updatePinRequestSchema = z.object({
    oldPin: z.string().regex(/^\d+$/, 'PIN must contain only digits'),
    newPin: z.string().regex(/^\d+$/, 'PIN must contain only digits'),
});
export const updatePinForbidResponseSchema = errorResponse('Invalid PIN information');
export const updatePinResponseSchema = successResponse();
